import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PhoneIcon, TrashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '../../../services/api';

/**
 * Vista global de admin: **todos** los números de llamada, de todos los clientes.
 *
 * No es el sitio donde una empresa conecta el suyo — eso vive en su propio
 * perfil (`CallPhonesSection`, dentro del bloque «Comunicación»), porque
 * Configuración es solo de admin y al cliente no se le da acceso. Aquí la
 * columna que importa es **Cliente**: es la única pantalla desde la que se ve de
 * un vistazo qué número es de quién.
 *
 * El código de verificación **le llega siempre al titular del número**, también
 * cuando lo da de alta un admin. Es justo lo que prueba que el número es suyo.
 */

const STATUS = {
  pending_verification: { label: 'Pendiente de verificar', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  active: { label: 'Activo', cls: 'bg-green-50 text-green-800 border-green-200' },
  disconnected: { label: 'Desconectado', cls: 'bg-gray-50 text-gray-500 border-gray-200' },
};

const CallPhones = () => {
  const [phones, setPhones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ phone_number: '', name: '', method: 'sms' });
  const [codes, setCodes] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/calls/phones');
      setPhones(Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const register = async (event) => {
    event.preventDefault();
    if (!form.phone_number.trim()) return;
    setBusyId('new');
    try {
      await api.post('/calls/phones', form);
      toast.success('Te hemos enviado un código a ese número');
      setForm({ phone_number: '', name: '', method: 'sms' });
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  const verify = async (phone) => {
    const code = (codes[phone._id] || '').trim();
    if (!code) return;
    setBusyId(phone._id);
    try {
      await api.post(`/calls/phones/${phone._id}/verify`, { code });
      toast.success('Número verificado, ya se puede llamar desde él');
      setCodes((c) => ({ ...c, [phone._id]: '' }));
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  const disconnect = async (phone) => {
    setBusyId(phone._id);
    try {
      await api.delete(`/calls/phones/${phone._id}`);
      toast.success('Número desconectado');
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className='rounded-lg border border-gray-200 bg-white p-5'>
      <div className='mb-1 flex items-center gap-2'>
        <PhoneIcon className='h-5 w-5 text-gray-400' aria-hidden='true' />
        <h3 className='text-lg font-semibold text-gray-900'>Números para llamar</h3>
      </div>

      {/*
        Esto no es letra pequeña: un cliente que espere recibir aquí las
        devoluciones de llamada se llevaría una sorpresa, y lo descubriría cuando
        un deudor le devolviera la llamada y no le entrara.
      */}
      <p className='mb-4 text-sm text-gray-500'>
        El número se queda en tu operador; nosotros solo lo usamos para{' '}
        <strong className='font-medium text-gray-700'>llamar</strong>. Las llamadas que te hagan a él seguirán
        entrando en tu centralita como hasta ahora.
      </p>

      <form onSubmit={register} className='mb-5 grid gap-2 sm:grid-cols-[1fr_1fr_auto_auto]'>
        <input
          type='text'
          value={form.phone_number}
          onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
          placeholder='+34 930 00 00 00'
          className='rounded-lg border border-gray-300 px-3 py-2 text-sm'
        />
        <input
          type='text'
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder='Nombre (opcional)'
          className='rounded-lg border border-gray-300 px-3 py-2 text-sm'
        />
        <select
          value={form.method}
          onChange={(e) => setForm({ ...form, method: e.target.value })}
          className='rounded-lg border border-gray-300 px-3 py-2 text-sm'
        >
          <option value='sms'>Código por SMS</option>
          <option value='call'>Código por llamada</option>
        </select>
        <button
          type='submit'
          disabled={busyId === 'new'}
          className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'
        >
          Añadir
        </button>
      </form>

      {loading && <p className='text-sm text-gray-400'>Cargando…</p>}
      {!loading && phones.length === 0 && (
        <p className='text-sm text-gray-400'>Todavía no has conectado ningún número.</p>
      )}

      <div className='overflow-x-auto rounded-lg border border-slate-200'>
        <table className='min-w-full text-sm'>
          <thead className='bg-slate-50 text-left text-slate-600'>
            <tr>
              <th className='px-4 py-3 font-medium'>Número</th>
              <th className='px-4 py-3 font-medium'>Cliente</th>
              <th className='px-4 py-3 font-medium'>Nombre</th>
              <th className='px-4 py-3 font-medium'>Estado</th>
              <th className='px-4 py-3 font-medium'>Entrantes</th>
              <th className='px-4 py-3 text-right font-medium'>Acción</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {phones.map((phone) => {
              const status = STATUS[phone.status] || STATUS.disconnected;
              return (
                <tr key={phone._id} className='hover:bg-slate-50'>
                  <td className='px-4 py-3 font-medium text-slate-900'>{phone.phone_number}</td>
                  {/* Sin cliente = número de ClaimUp. Decirlo evita que alguien
                      lo tome por un número huérfano y lo desconecte. */}
                  <td className='px-4 py-3 text-slate-700'>{phone.company_name || 'ClaimUp'}</td>
                  <td className='px-4 py-3 text-slate-500'>{phone.nombre || '—'}</td>
                  <td className='px-4 py-3'>
                    <span className={`rounded-lg border px-2 py-0.5 text-xs font-medium ${status.cls}`}>
                      {status.label}
                    </span>
                  </td>
                  <td className='px-4 py-3 text-slate-500'>{phone.inbound_capable ? 'Sí' : 'No'}</td>
                  <td className='px-4 py-3 text-right'>
                    {phone.status === 'pending_verification' && (
                      <span className='flex items-center justify-end gap-2'>
                        <input
                          type='text'
                          inputMode='numeric'
                          value={codes[phone._id] || ''}
                          onChange={(e) => setCodes({ ...codes, [phone._id]: e.target.value })}
                          placeholder='Código'
                          className='w-28 rounded-lg border border-slate-300 px-2 py-1 text-sm'
                        />
                        <button
                          type='button'
                          onClick={() => verify(phone)}
                          disabled={busyId === phone._id}
                          className='rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50'
                        >
                          Verificar
                        </button>
                      </span>
                    )}
                    {phone.status === 'active' && (
                      <button
                        type='button'
                        onClick={() => disconnect(phone)}
                        disabled={busyId === phone._id}
                        className='inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50'
                      >
                        <TrashIcon className='h-4 w-4' aria-hidden='true' />
                        Desconectar
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <button
        type='button'
        onClick={load}
        className='mt-4 flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700'
      >
        <ArrowPathIcon className='h-4 w-4' aria-hidden='true' />
        Actualizar
      </button>
    </div>
  );
};

export default CallPhones;
